import { useEffect, useState } from "react"
import { useRouter } from "@/lib/router-helpers"
import { Lock } from "lucide-react"
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp"
import { useVerifyOTP } from "@/hooks/use-auth"
import { getPendingPhone, clearPendingPhone } from "@/lib/auth"
import { useAuth } from "@/components/auth-provider"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"
import ProgressPill from "@/components/progress-pill"
//Test

export default function OtherOtpVerifyPage() {
  const router = useRouter()
  const { login } = useAuth()
  const [otp, setOtp] = useState("")
  const [mobile, setMobile] = useState<string | null>(null)
  const verifyOTPMutation = useVerifyOTP()
  const { t } = useTranslation()


  useEffect(() => {
    const p = getPendingPhone()
    setMobile(p)
    if (!p) router.push(`/auth/verify`)
  }, [router])

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    if (otp.length !== 5 || !mobile) {
      toast.error(t("Please enter a valid 5-digit OTP"))
      return
    }

    try {
      const result = await verifyOTPMutation.mutateAsync({ mobile, otp })

      // If user and token are returned, user is registered
      if (result.message && result.status === "success") {
        login(result.message)
        clearPendingPhone()
        toast.success(t("Login successful"))
        router.push(`/`)
      }
    } catch (error: any) {
      // If 401, user is not registered - redirect to signup
      if (error.status === 401 || error.message?.includes("not registered")) {
        window.localStorage.setItem("qetero.unregistered but verified", mobile)

        setTimeout(() => {
          window.localStorage.removeItem("qetero.unregistered but verified")
        }, 2 * 60 * 1000)

        toast.info(t("Account not found. Please create an account."))
        router.push(`/auth/register`)
      } else {
        toast.error(error.message || t("Invalid OTP. Please try again."))
      }
    }
  }

  return (
    <>
         <div className="mt-1 mb-3">
              <ProgressPill value={90} />
            </div>
      
      <div className="px-4 pb-8 ">
        <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-4 relative max-w-lg mx-auto md:px-9">
          {/* Top gradient accent to match design */}
          <div className="absolute left-0 right-0 top-0 h-2.5 rounded-t-[14px] bg-[linear-gradient(90deg,#ff0a7a_0%,#ff1e79_100%)]" />
          <div className="flex flex-col items-center text-center">
            <div className="w-16 h-16 rounded-full bg-pink-50 flex items-center justify-center shadow-sm mb-8 mt-10">
              <Lock className="w-7 h-7 text-pink-600" />
            </div>
            <h3 className="text-sm font-bold text-gray-900">{t("VerifyYourNumber")}</h3>
            <p className="text-[11px] text-gray-400 mt-1">
              {t("Verification code sent to a phone number")} <br /> <span className="text-pink-600 font-semibold">{mobile ?? ""}</span>
            </p>
          </div>

          <form onSubmit={handleVerify} className="mt-7">
            <label className="block text-[13px] font-semibold text-gray-800 mb-4">{t("OTP")}</label>
            <InputOTP
              maxLength={5}
              value={otp}
              onChange={setOtp}
              disabled={verifyOTPMutation.isPending}
              containerClassName="flex  justify-between px-4  items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-300 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
              className=""
            >
              <InputOTPGroup >
                <InputOTPSlot index={0} className="h-13 w-13 md:h-17 md:w-17 text-lg focus:outline-none focus:border-transparent focus-visible:ring-2 focus-visible:ring-pink-500/40" />
              </InputOTPGroup>
              <InputOTPGroup>
                <InputOTPSlot index={1} className="h-13 w-13 md:h-17 md:w-17 text-lg" />
              </InputOTPGroup>
              <InputOTPGroup>
                <InputOTPSlot index={2} className="h-13 w-13 md:h-17 md:w-17 text-lg" />
              </InputOTPGroup>
              <InputOTPGroup>
                <InputOTPSlot index={3} className="h-13 w-13 md:h-17 md:w-17 text-lg" />
              </InputOTPGroup>
              <InputOTPGroup>
                <InputOTPSlot index={4} className="h-13 w-13 md:h-17 md:w-17 text-lg" />
              </InputOTPGroup>
            </InputOTP>
            <p className="text-[10px] text-gray-400 mt-2">{t("Enter the verification code sent.")}</p>

            <button
              type="submit"
              disabled={verifyOTPMutation.isPending || otp.length !== 5}
              className="mt-8 w-full h-12 rounded-full bg-pink-600 text-white text-sm font-semibold shadow-[0_6px_16px_rgba(230,0,119,0.3)] hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed md:mt-13"
            >
              {verifyOTPMutation.isPending ? t("verifying") : t("verify")}
            </button>

            <p className="text-[10px] text-gray-500 text-center mt-3">
              {t("By continuing, you agree to our")} <span className="text-pink-600 font-semibold">{t("Terms of Service")}</span> {t("and")}
              <span className="text-pink-600 font-semibold">{t("Privacy Policy")}</span>
            </p>
          </form>
        </div>
      </div>
    </>
  )
}
